export type PublicEnvironment = Record<string, string | boolean | undefined>
export type Capabilities = Readonly<Record<string, boolean | Readonly<Record<string, boolean>>>>

export function isCatalogueOnly(capabilities: Capabilities): boolean {
  return capabilities.techniques === true && capabilities.community === false &&
    Object.entries(capabilities).every(([key, value]) => key === 'techniques' ||
      (typeof value === 'boolean' ? value === false : Object.values(value).every(flag => flag === false)))
}

export function required(env: PublicEnvironment, name: string): string {
  const value = env[name]
  if (typeof value !== 'string' || !value.trim()) throw new Error(`${name} is not set. Render the environment with scripts/deploy frontend <tenant> <env> --validate-only --write-env.`)
  return value.trim()
}

function endpoint(env: PublicEnvironment, name: string, protocols = ['http:', 'https:']): string {
  const value = required(env, name)
  let url: URL
  try { url = new URL(value) } catch { throw new Error(`${name} must be an absolute URL`) }
  if (!protocols.includes(url.protocol) || url.username || url.password) throw new Error(`${name} must be a public ${protocols.join('/')} URL`)
  return value.replace(/\/+$/, '')
}

export function resolveTenantConfig(env: PublicEnvironment) {
  const environment = required(env, 'VITE_ENVIRONMENT')
  if (!['local', 'development', 'production'].includes(environment)) throw new Error('VITE_ENVIRONMENT must be local, development or production')
  const id = required(env, 'VITE_TENANT_ID')
  const brandName = required(env, 'VITE_BRAND_NAME')
  const themeId = required(env, 'VITE_THEME_ID')
  const storagePrefix = required(env, 'VITE_STORAGE_PREFIX')
  const websiteUrl = endpoint(env, 'VITE_WEBSITE_URL')
  const websiteHost = new URL(websiteUrl).host
  const supportEmail = required(env, 'VITE_SUPPORT_EMAIL')
  if (!/^[^\s@]+@[^\s@]+$/.test(supportEmail)) throw new Error('VITE_SUPPORT_EMAIL must be an email address')
  const rawCapabilities = required(env, 'VITE_CAPABILITIES')
  let capabilities: Capabilities
  try {
    const parsed: unknown = JSON.parse(rawCapabilities)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error()
    for (const value of Object.values(parsed)) {
      if (typeof value === 'boolean') continue
      if (!value || typeof value !== 'object' || Array.isArray(value) || Object.values(value).some(v => typeof v !== 'boolean')) throw new Error()
    }
    capabilities = parsed as Capabilities
  } catch { throw new Error('VITE_CAPABILITIES must be an object of boolean capability flags') }
  const catalogueOnly = isCatalogueOnly(capabilities)
  const platformApiUrl = catalogueOnly ? '' : endpoint(env, 'VITE_API_URL')
  const communityApiUrl = capabilities.community === true ? endpoint(env, 'VITE_COMMUNITY_API_URL') : undefined
  const webSocketUrl = catalogueOnly ? '' : endpoint(env, 'VITE_WS_URL', ['ws:', 'wss:'])
  const firebase = environment === 'local' || catalogueOnly ? null : {
    apiKey: required(env, 'VITE_FIREBASE_API_KEY'),
    authDomain: required(env, 'VITE_FIREBASE_AUTH_DOMAIN'),
    projectId: required(env, 'VITE_FIREBASE_PROJECT_ID'),
    storageBucket: required(env, 'VITE_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: required(env, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
    appId: required(env, 'VITE_FIREBASE_APP_ID'),
    ...(env.VITE_FIREBASE_MEASUREMENT_ID ? { measurementId: String(env.VITE_FIREBASE_MEASUREMENT_ID) } : {}),
  }
  return { id, environment, brandName, brandTitle: brandName.charAt(0).toUpperCase() + brandName.slice(1), themeId,
    storagePrefix, websiteUrl, websiteHost,
    websiteLabel: websiteHost.toLowerCase().startsWith(`${brandName.toLowerCase()}.`) ? brandName + websiteHost.slice(brandName.length) : websiteHost,
    supportEmail, platformApiUrl, communityApiUrl, webSocketUrl, firebase, capabilities } as const
}
export type TenantConfig = ReturnType<typeof resolveTenantConfig>
export type TenantId = TenantConfig['id']
